import { Chip, Paper, TextField, Tooltip } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState, useContext } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import { createTag as createTagApi, listTagsSimple, syncTags as syncTagsApi } from "../../services/tags.api";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const MAX_TAG_LABEL_CHARS = 30;

/** Texto visible en chips; tooltip muestra el nombre completo */
const formatTagChipLabel = (name) => {
    const upper = (name || "").toUpperCase();
    if (upper.length <= MAX_TAG_LABEL_CHARS) return upper;
    return `${upper.slice(0, MAX_TAG_LABEL_CHARS)}…`;
};

export function TagsContainer({ ticket }) {

    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const isMounted = useRef(true);
    const { user } = useContext(AuthContext);
    const socketManager = useContext(SocketContext);

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            loadTags().then(() => {
                if (Array.isArray(ticket.tags)) {
                    setSelecteds(ticket.tags);
                } else {
                    setSelecteds([]);
                }
            });
        }
    }, [ticket]);

    // ✅ SOCKET LISTENER PARA ACTUALIZAR ETIQUETAS EN TIEMPO REAL
    useEffect(() => {
        if (!socketManager || !user) return;

        const socket = socketManager.getSocket(user.companyId);
        if (!socket) return;

        const handleTagUpdate = (data) => {
            if ((data.action === "create" || data.action === "update") && data.tag) {
                // ✅ Recargar la lista de etiquetas cuando se crea/actualiza una nueva
                loadTags();
            }
        };

        socket.on("tag", handleTagUpdate);

        return () => {
            if (socket && typeof socket.off === 'function') {
                socket.off("tag", handleTagUpdate);
            }
        };
    }, [socketManager, user]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await createTagApi(data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await listTagsSimple({ kanbanActiveOnly: "1" });
            setTags(data);
        } catch (err) {
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await syncTagsApi(data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (isString(item)) {
                        const newTag = await createTag({ name: item })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        setSelecteds(optionsChanged);
        await syncTags({ ticketId: ticket.id, tags: optionsChanged });
    }

    return (
        <Paper style={{ padding: 12 }}>
            <Autocomplete
                multiple
                size="small"
                options={tags}
                value={selecteds}
                freeSolo
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => option.name}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const fullName = option.name || "";
                        const { key, ...tagProps } = getTagProps({ index });
                        const chip = (
                            <Chip
                                variant="outlined"
                                style={{
                                    background: option.color || '#eee',
                                    color: "#FFF",
                                    marginRight: 1,
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    fontSize: "0.8em",
                                    whiteSpace: "nowrap",
                                    maxWidth: 220
                                }}
                                label={formatTagChipLabel(fullName)}
                                {...tagProps}
                                size="small"
                            />
                        );
                        const wrapped =
                            fullName.length > MAX_TAG_LABEL_CHARS ? (
                                <Tooltip title={fullName} placement="top" arrow>
                                    <span style={{ display: "inline-flex" }}>{chip}</span>
                                </Tooltip>
                            ) : (
                                chip
                            );
                        return (
                            <React.Fragment key={key}>
                                {wrapped}
                            </React.Fragment>
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Tags" />
                )}
                PaperComponent={({ children }) => (
                    <Paper style={{ width: 400, marginLeft: 12 }}>
                        {children}
                    </Paper>
                )}
            />
        </Paper>
    )
}